import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../api/apiClient';
import debtorApi from '../../api/debtorApi';
import creditApi from '../../api/creditApi';
import FadeIn from '../../components/ui/FadeIn';
import PortalHeader from '../../components/portal/PortalHeader';
import QuickStats from '../../components/portal/QuickStats';
import ModuleCards from '../../components/portal/ModuleCards';
import NotificationCard from '../../components/portal/NotificationCard';
import QuickActions from '../../components/portal/QuickActions';
import { useAuth } from '../../context/AuthContext';

export default function Portal() {
  const { token, user } = useAuth();
  const [stats, setStats] = useState({
    employees: 0,
    debtors: 0,
    creditApplications: 0,
    pendingApprovals: 0,
  });
  const [notifications, setNotifications] = useState([]);
  const [, setLoading] = useState(true);

  const loadDashboardData = useCallback(async () => {
    try {
      // Only load data if user is admin
      // Regular users get a basic welcome dashboard
      if (user?.role === 'admin') {
        try {
          const [employeesData, debtorsData, creditData] = await Promise.all([
            apiClient.get('/hris/employees').catch(() => ({ employees: [] })),
            debtorApi.getAll().catch(() => []),
            creditApi.getAll().catch(() => []),
          ]);

          const employees = employeesData.employees || employeesData || [];
          const debtors = Array.isArray(debtorsData) ? debtorsData : [];
          const applications = Array.isArray(creditData) ? creditData : [];

          const pending = applications.filter(
            (a: any) => a.status === 'approval' || a.status === 'pending_approval'
          );

          setStats({
            employees: employees.length,
            debtors: debtors.length,
            creditApplications: applications.length,
            pendingApprovals: pending.length,
          });
        } catch (error) {
          // Even for admin, set default stats on error
          setStats({
            employees: 0,
            debtors: 0,
            creditApplications: 0,
            pendingApprovals: 0,
          });
        }
      } else {
        // Regular users see basic dashboard without protected data
        setStats({
          employees: 0,
          debtors: 0,
          creditApplications: 0,
          pendingApprovals: 0,
        });
      }

      setNotifications([]);
    } catch (error) {
      // Set default stats on any error
      setStats({
        employees: 0,
        debtors: 0,
        creditApplications: 0,
        pendingApprovals: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    if (token && user) {
      loadDashboardData();
    }
  }, [token, user, loadDashboardData]);

  return (
    <div className="space-y-4 md:space-y-6">
      <FadeIn delay={0}>
        <PortalHeader />
      </FadeIn>
      <FadeIn delay={0.1}>
        <QuickStats stats={stats} />
      </FadeIn>
      <FadeIn delay={0.2}>
        <ModuleCards stats={stats} />
      </FadeIn>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <FadeIn delay={0.3}>
          <NotificationCard notifications={notifications} />
        </FadeIn>
        <FadeIn delay={0.4}>
          <QuickActions pendingApprovals={stats.pendingApprovals} />
        </FadeIn>
      </div>
    </div>
  );
}
